import React, { useEffect, useState } from "react";
import { confirmable } from "react-confirm";
import { Row, message, Input, Select, Button, Tag } from "antd";
import Modal from "antd/lib/modal/Modal";
import "./SmsModal.css";
import { fetchAudienceList } from "services/audienceService";
import { apiSmsSendBulk } from "services/smsService";

const SMS_SEGMENT_SIZE = 160;

function SmsModal(props) {
  const { show, proceed, title } = props;

  const [audienceList, setAudienceList] = useState([]);
  const [selectedAudiences, setSelectedAudiences] = useState([]);
  const [messageText, setMessageText] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [sendResults, setSendResults] = useState(null);

  // Load SMS audiences when modal opens
  useEffect(() => {
    async function loadAudiences() {
      const token = props?.token;
      const clubId = props?.club_id;
      if (token && show) {
        const list = await fetchAudienceList(token, clubId);
        // Filter to SMS audiences only (channel === "sms")
        const smsList = (Array.isArray(list) ? list : []).filter(
          (a) => a.channel === "sms"
        );
        setAudienceList(smsList);
      }
    }
    if (show) {
      loadAudiences();
    }
  }, [show, props?.token]);

  // Reset when modal closes
  useEffect(() => {
    if (!show) {
      setSelectedAudiences([]);
      setMessageText("");
      setSendResults(null);
    }
  }, [show]);

  // Compute character/segment info
  const charCount = messageText.length;
  const segmentCount = charCount === 0 ? 0 : Math.ceil(charCount / SMS_SEGMENT_SIZE);

  // Compute total recipients from selected audiences
  const totalRecipients = selectedAudiences.reduce((sum, audienceId) => {
    const aud = audienceList.find((a) => a.id === audienceId);
    return sum + (aud?.email_count || 0); // email_count holds member count
  }, 0);

  const totalMessages = segmentCount * totalRecipients;
  const smsCost = props?.smsCost || 0;
  const estimatedCost = totalMessages * smsCost;

  // Collect all phone numbers from selected audiences
  const getPhoneNumbers = () => {
    const phones = new Set();
    selectedAudiences.forEach((audienceId) => {
      const audience = audienceList.find((a) => a.id === audienceId);
      if (audience && Array.isArray(audience.emails)) {
        // For SMS audiences, the "emails" field contains phone numbers
        audience.emails.forEach((phone) => phones.add(phone));
      }
    });
    return Array.from(phones);
  };

  const onSend = async () => {
    if (selectedAudiences.length === 0) {
      message.error("Please select at least one SMS audience");
      return;
    }
    if (!messageText.trim()) {
      message.error("Please enter a message");
      return;
    }

    const phoneNumbers = getPhoneNumbers();
    if (phoneNumbers.length === 0) {
      message.error("Selected audiences have no phone numbers");
      return;
    }

    setIsSending(true);
    try {
      const result = await apiSmsSendBulk({
        token: props.token,
        phone_numbers: phoneNumbers,
        message_text: messageText.trim(),
      });

      // Include audience names in results for the table
      const audienceNames = selectedAudiences.map((id) => {
        const aud = audienceList.find((a) => a.id === id);
        return aud?.name || "SMS";
      });
      const enrichedResult = { ...result, audience_names: audienceNames };
      setSendResults(enrichedResult);
      message.success(
        `SMS sent! ${result?.successful || phoneNumbers.length} delivered, ${result?.failed || 0} failed.`
      );
    } catch (error) {
      console.error("[SendSMS] error:", error);
      message.error(error || "Failed to send SMS");
    } finally {
      setIsSending(false);
    }
  };

  const onClose = () => {
    proceed(sendResults ? { success: true, results: sendResults, messageText } : null);
  };

  return (
    <Modal
      open={show}
      destroyOnClose={true}
      onCancel={onClose}
      className="sms-modal"
      closable={true}
      maskClosable={false}
      title={sendResults ? "SMS Results" : "Send SMS"}
      footer={null}
      width={520}
    >
      {sendResults ? (
        // Results view
        <div className="sms-results">
          <div className="sms-results-summary">
            <div className="sms-result-stat">
              <span className="sms-result-label">Total Sent</span>
              <span className="sms-result-value success">
                {sendResults.successful ?? sendResults.total_sent ?? 0}
              </span>
            </div>
            <div className="sms-result-stat">
              <span className="sms-result-label">Failed</span>
              <span className="sms-result-value failed">
                {sendResults.failed ?? sendResults.total_failed ?? 0}
              </span>
            </div>
          </div>

          {Array.isArray(sendResults.results) && sendResults.results.length > 0 && (
            <div className="sms-results-detail">
              <div style={{ fontWeight: 600, marginBottom: 8 }}>Details:</div>
              <div className="sms-results-list">
                {sendResults.results.map((r, i) => (
                  <div key={i} className="sms-result-row">
                    <span className="sms-result-phone">{r.phone_number}</span>
                    <Tag color={r.status === "sent" ? "green" : "red"}>
                      {r.status === "sent" ? "Sent" : "Failed"}
                    </Tag>
                    {r.error && <span className="sms-result-error">{r.error}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div style={{ marginTop: 16, textAlign: "right" }}>
            <Button type="primary" onClick={onClose}>
              Done
            </Button>
          </div>
        </div>
      ) : (
        // Compose view
        <>
          <Row style={{ marginTop: 10 }}>
            <div className="sms-field-label required">To (SMS Audiences)</div>
            <Select
              mode="multiple"
              placeholder="Select SMS audiences"
              allowClear
              value={selectedAudiences}
              onChange={(val) => setSelectedAudiences(val || [])}
              options={audienceList.map((a) => ({
                label: `${a.name} (${a.email_count || 0} recipients)`,
                value: a.id,
              }))}
              style={{ width: "100%" }}
            />
            {audienceList.length === 0 && (
              <div className="sms-hint">
                No SMS audiences found. Create an SMS audience from the Audience
                Dashboard first.
              </div>
            )}
          </Row>

          <Row style={{ marginTop: 16 }}>
            <div className="sms-field-label required">Message</div>
            <Input.TextArea
              rows={5}
              maxLength={1600}
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
              placeholder="Type your SMS message..."
              className="sms-textarea"
            />
          </Row>

          <div className="sms-stats-row">
            <div className="sms-stat">
              <span className="sms-stat-label">Characters:</span>
              <span className={charCount > SMS_SEGMENT_SIZE ? "sms-stat-value warn" : "sms-stat-value"}>
                {charCount} / {SMS_SEGMENT_SIZE}
              </span>
            </div>
            <div className="sms-stat">
              <span className="sms-stat-label">Segments:</span>
              <span className="sms-stat-value">{segmentCount}</span>
            </div>
            <div className="sms-stat">
              <span className="sms-stat-label">Recipients:</span>
              <span className="sms-stat-value">{totalRecipients}</span>
            </div>
            <div className="sms-stat">
              <span className="sms-stat-label">Total Messages:</span>
              <span className="sms-stat-value">{totalMessages}</span>
            </div>
            {smsCost > 0 && (
            <div className="sms-stat">
              <span className="sms-stat-label">Estimated Cost:</span>
              <span className="sms-stat-value">${estimatedCost.toFixed(2)}</span>
            </div>
            )}
          </div>

          <div className="sms-modal-buttons">
            <Button onClick={onClose}>Cancel</Button>
            <Button
              type="primary"
              onClick={onSend}
              loading={isSending}
              disabled={isSending}
            >
              {isSending ? "Sending..." : "Send SMS"}
            </Button>
          </div>
        </>
      )}
    </Modal>
  );
}

export default confirmable(SmsModal);
